import { useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"

import { Button, Container, NewsletterModal, SvgIcon, type FC, type iconKeys } from ".."
import type { ExtendedCTAType } from "../../graphql/types"

type ExtendedCTAPerkItemProps = {
  title: string
  icon: string
}

const ExtendedCTA: FC<ExtendedCTAType> = ({ id, title, subtitle, description, items }) => {
  return (
    <Container py={10}>
      <ExtendedCTAStylesSection>
        <Box
          maxWidth={{ lg: 574 }}
          minHeight={{ lg: 440 }}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          mb={{ xs: 15, md: 0 }}
        >
          <ExtendedCTASubtitle subtitle={subtitle} />
          <ExtendedCTATitle title={title} />
          <ExtendedCTADescription description={description} />
          {/* NOTE: Newsletter CTA modal has been hard-coded */}
          <ExtendedCTAButton title="Sign Up Today" />
        </Box>
        <StyledBox minWidth={{ xs: "100%", lg: 404 }}>
          {items?.map(({ title, icon }, i) => (
            <ExtendedCTAPerkItem key={i} title={title} icon={icon} />
          ))}
        </StyledBox>
      </ExtendedCTAStylesSection>
    </Container>
  )
}

export default ExtendedCTA

/** A sub-component for the title */
const ExtendedCTATitle: FC<{ title: string }> = ({ title }) => (
  <Typography
    component="h2"
    variant="heading.2xl"
    mb={5}
    sx={(theme) => ({
      color: theme.palette["secondary.900"].main,
    })}
  >
    {title}
  </Typography>
)

/** A sub-component for the title */
const ExtendedCTASubtitle: FC<{ subtitle: string }> = ({ subtitle }) => (
  <Typography
    component="h3"
    variant="body.lg"
    mb={5}
    sx={(theme) => ({
      color: theme.palette["secondary.800"].main,
    })}
  >
    {subtitle}
  </Typography>
)

/** A sub-component for the description */
const ExtendedCTADescription: FC<{ description: string }> = ({ description }) => (
  <Typography
    component="div"
    variant="body.lg"
    sx={(theme) => ({
      color: theme.palette["secondary.900"].main,
    })}
  >
    <Box dangerouslySetInnerHTML={{ __html: description }} />
  </Typography>
)

/** A sub-component for the description */
const ExtendedCTAButton: FC<{ title: string }> = ({ title }) => {
  const [openNewsletter, setOpenNewsletter] = useState(false)

  return (
    <>
      <Box mt={{ xs: 15, md: 18 }}>
        <Button
          variant="neutral.secondary"
          onClick={() => setOpenNewsletter(true)}
          label={title}
          sx={(theme) => ({
            minWidth: 365,

            [theme.breakpoints.down("lg")]: {
              minWidth: "100%",
            },
          })}
        />
      </Box>
      <NewsletterModal open={openNewsletter} onClose={() => setOpenNewsletter(false)} />
    </>
  )
}

/** A sub-component for the perk item */
const ExtendedCTAPerkItem: FC<ExtendedCTAPerkItemProps> = ({ title, icon }) => (
  <Box display="flex" gap={4.5} alignItems="center">
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        width: { xs: 14, lg: 20 },
        aspectRatio: "1",
      }}
    >
      <SvgIcon
        variant={icon as iconKeys}
        size={{ xs: 14, md: 22 }}
        sx={{ fill: (theme) => theme.palette["secondary.800"].main }}
      />
    </Stack>

    <Typography
      variant="body.md"
      component="h2"
      sx={(theme) => ({
        color: theme.palette["secondary.800"].main,
      })}
    >
      {title}
    </Typography>
  </Box>
)

/** styled components */
const ExtendedCTAStylesSection = styled(Box)(
  ({ theme }) => `
    display: grid;
    grid-template-columns: auto auto;
    column-gap: ${theme.spacing(20)};
    justify-content: space-between;
    justify-items: center;
    min-height: 600px;
    border-radius: ${theme.spacing(1.5)};
    padding: ${theme.spacing(20, 32.5)};
    background-color: ${theme.palette["secondary.200"].main};

    ${theme.breakpoints.down("xl")} {
      padding: ${theme.spacing(20)};
    }

    ${theme.breakpoints.down("lg")} {
      grid-template-columns: 1fr;
      min-height: auto;
      padding: ${theme.spacing(10, 6)};
    }


  `
)

const StyledBox = styled(Box)(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(10)};
    justify-content: center;
    padding: ${theme.spacing(30)};
    height: 100%;
    border-left: 1px solid ${theme.palette["secondary.300"].main};

    ${theme.breakpoints.down("lg")} {
      border-left: none;
      border-top: 1px solid ${theme.palette["secondary.300"].main};
      padding: ${theme.spacing(10, 3, 0)};
      gap: ${theme.spacing(5)};
      align-items: start;
      justify-content: start;
      height: auto;
    }
  `
)
